import {
   parseString,
   parseIntValue,
   parseSimpleObjectArray,
   parseSimpleObjectArrayLegacyCompat,
   parseWorldmapMetric,
   parseDateTime,
   parseBoolean,
} from './urlToStore';
import { getSingleUrlParam, getValueOnlyUrlParam, getSimpleArrayUrlParam, getDateTimeUrlParam } from './storeToUrl';
import { WORLDMAP_METRIC } from '../components/widgets/world/WorldMapConstants';
import { BREAKDOWN_VIEW, HOME_VIEW } from '../constants/general';
import { eventViews } from '../constants/events';
import { URL_PARAMS } from './urlParamNames';

export const POST_PROCESSING = {
   MOMENT_CONVERSION: 'MOMENT_CONVERSION',
   EXTRACT_INTERVAL_VALUE: 'EXTRACT_INTERVAL_VALUE',
};

export const urlSyncSettings = {
   // Org
   [URL_PARAMS.ORGA_HASH]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'filter',
      storeName: 'hash',
   },
   [URL_PARAMS.ORGA_NAME]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'filter',
      storeName: 'name',
   },
   [URL_PARAMS.ORIGIN_HASH]: {
      // ORIGIN_HASH must be under ORG_HASH
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      adminTargetProp: 'hash',
   },
   [URL_PARAMS.ORIGIN_NAME]: {
      // ORIGIN_NAME must be under ORG_NAME
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      adminTargetProp: 'name',
   },
   // Filter
   [URL_PARAMS.COUNTRIES]: {
      parseFn: parseSimpleObjectArrayLegacyCompat,
      urlFn: getSimpleArrayUrlParam,
      group: 'filter',
      storeName: 'countries',
   },
   [URL_PARAMS.TAGS]: {
      parseFn: parseSimpleObjectArrayLegacyCompat,
      urlFn: getSimpleArrayUrlParam,
      group: 'filter',
      storeName: 'tags',
   },
   [URL_PARAMS.EVENTS]: {
      parseFn: parseSimpleObjectArrayLegacyCompat,
      urlFn: getSimpleArrayUrlParam,
      group: 'filter',
      storeName: 'events',
      views: eventViews,
   },
   [URL_PARAMS.STREAM_NAMES]: {
      parseFn: parseSimpleObjectArrayLegacyCompat,
      urlFn: getSimpleArrayUrlParam,
      group: 'filter',
      storeName: 'streamNames',
   },
   [URL_PARAMS.USAGE_SWITCH]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'usageSwitch',
      views: [HOME_VIEW, BREAKDOWN_VIEW],
   },
   // Time
   [URL_PARAMS.TIME_ID]: {
      parseFn: parseIntValue,
      urlFn: getSingleUrlParam,
      group: 'timespan',
      storeName: 'id',
   },
   [URL_PARAMS.TIME_INTERVAL]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'timespan',
      storeName: 'interval',
   },
   [URL_PARAMS.TIME_START]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'timespan',
      storeName: 'gte',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   [URL_PARAMS.TIME_END]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'timespan',
      storeName: 'lt',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   // Worldmap
   [URL_PARAMS.WORLDMAP_METRIC]: {
      parseFn: parseWorldmapMetric,
      urlFn: getValueOnlyUrlParam,
      group: '',
      storeName: 'worldmapMetric',
   },
   [URL_PARAMS.WORLDMAP_PI_SUB]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'worldmapSubSwitch',
      storeName: WORLDMAP_METRIC.PI,
   },
   [URL_PARAMS.WORLDMAP_ABR_SUB]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'worldmapSubSwitch',
      storeName: WORLDMAP_METRIC.ABR_PLAYTIME,
   },
   [URL_PARAMS.WORLDMAP_CDN]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'worldmapOptions',
      storeName: 'cdn',
   },
   [URL_PARAMS.WORLDMAP_VIEW]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'worldmapView',
   },
   [URL_PARAMS.WORLDMAP_REGION_ZOOM]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'worldmapRegionZoom',
   },
   // Troubleshooting — general
   [URL_PARAMS.TROUBLE_START]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleTimespan',
      storeName: 'start',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   [URL_PARAMS.TROUBLE_END]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleTimespan',
      storeName: 'end',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   [URL_PARAMS.TROUBLE_INTERVAL]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleTimespan',
      storeName: 'troubleInterval',
      postProcessing: POST_PROCESSING.EXTRACT_INTERVAL_VALUE,
   },
   [URL_PARAMS.TROUBLE_MODE]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'troubleMode',
   },
   // Troubleshooting — Ingest
   [URL_PARAMS.TROUBLE_INGEST_MODE]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'troubleIngestMode',
   },
   [URL_PARAMS.TROUBLE_INGEST_INPUT]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleIngestInput',
      storeName: 'ingestInput',
   },
   [URL_PARAMS.TROUBLE_INGEST_STREAM]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleIngestStream',
      storeName: 'ingestStream',
   },
   [URL_PARAMS.TROUBLE_INGEST_PUBLISH_START]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPublish',
      storeName: 'publishStart',
   },
   [URL_PARAMS.TROUBLE_INGEST_PUBLISH_END]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPublish',
      storeName: 'publishEnd',
   },
   [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_START]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPublish',
      storeName: 'publishFineStart',
   },
   [URL_PARAMS.TROUBLE_INGEST_PUBLISH_FINE_END]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPublish',
      storeName: 'publishFineEnd',
   },
   // Troubleshooting — Playout
   [URL_PARAMS.TROUBLE_PLAYOUT_INPUT]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troublePlayoutInput',
      storeName: 'userId',
   },
   [URL_PARAMS.TROUBLE_PLAYOUT_STREAM]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'troublePlayoutStream',
   },
   [URL_PARAMS.TROUBLE_PLAYOUT_ID]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleSelectedPlayout',
      storeName: 'playerId',
   },
   [URL_PARAMS.TROUBLE_PLAYOUT_FINE_START]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPlayout',
      storeName: 'playoutFineStart',
   },
   [URL_PARAMS.TROUBLE_PLAYOUT_FINE_END]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedPlayout',
      storeName: 'playoutFineEnd',
   },
   // Misuse
   [URL_PARAMS.MISUSE_START]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'misuseTimespan',
      storeName: 'start',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   [URL_PARAMS.MISUSE_END]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'misuseTimespan',
      storeName: 'end',
      postProcessing: POST_PROCESSING.MOMENT_CONVERSION,
      alwaysSync: true,
   },
   [URL_PARAMS.MISUSE_STREAMS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'streams',
   },
   [URL_PARAMS.MISUSE_IPS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'ips',
   },
   [URL_PARAMS.MISUSE_JTIS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'jtis',
   },
   [URL_PARAMS.MISUSE_REFERRERS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'referrers',
   },
   [URL_PARAMS.MISUSE_COUNTRIES]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'countries',
   },
   [URL_PARAMS.MISUSE_PROVIDERS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'providers',
   },
   [URL_PARAMS.MISUSE_USERS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'users',
   },
   [URL_PARAMS.MISUSE_TAGS]: {
      parseFn: parseSimpleObjectArray,
      urlFn: getSimpleArrayUrlParam,
      group: 'misuseFilter',
      storeName: 'tags',
   },
   [URL_PARAMS.MISUSE_AGG_TERM]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'misuseAggTerm',
   },
   [URL_PARAMS.MISUSE_CC_STATE]: {
      parseFn: parseBoolean,
      urlFn: getSingleUrlParam,
      group: '',
      storeName: 'misuseCcState',
   },
   // Alert
   [URL_PARAMS.ALERT_ID]: {
      parseFn: parseString,
      urlFn: getSingleUrlParam,
      group: 'troubleSelectedAlert',
      storeName: 'alertId',
   },
   [URL_PARAMS.ALERT_FINE_START]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedAlert',
      storeName: 'alertFineStart',
   },
   [URL_PARAMS.ALERT_FINE_END]: {
      parseFn: parseDateTime,
      urlFn: getDateTimeUrlParam,
      group: 'troubleSelectedAlert',
      storeName: 'alertFineEnd',
   },
   // scheme:
   // [URL_PARAMS.XYZ]: {
   //    parseFn: fn to parse url param value (string) to redux store value,
   //    urlFn: fn to get url param value from redux store value,
   //    group: param group name in redux store,
   //    storeName: param name in redux store,
   // }
};
